import { useEffect, useState } from "react";

import { Chevron } from "@/components/ui/Chevron";
import { explainStep, submitDeployConfirmation } from "@/lib/api/sessions";
import type { DeploymentPlan } from "@/lib/api/types";

/**
 * What-if diff viewer + the mandatory deploy confirmation gate.
 *
 * Layout:
 *   - Planned-resources table (action badges)
 *   - Raw what-if diff in a scrollable code block
 *   - Footer: cancel / confirm-and-deploy buttons
 *
 * The diff text comes verbatim from `az deployment group what-if --no-pretty-print`
 * on the server side, which is the most trustworthy representation of
 * what will land in the user's Azure subscription.
 */
export function PlanViewer({
  sessionId,
  plan,
}: {
  sessionId: string;
  plan: DeploymentPlan;
}) {
  const [submitting, setSubmitting] = useState<"accept" | "reject" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [explanation, setExplanation] = useState<string | null>(null);
  const [explanationLoading, setExplanationLoading] = useState(true);

  // Step 4: LLM commentary for the plan step.
  useEffect(() => {
    const ctrl = new AbortController();
    setExplanationLoading(true);
    explainStep(sessionId, { step: "plan" }, ctrl.signal)
      .then((r) => setExplanation(r.text))
      .catch(() => setExplanation(null))
      .finally(() => setExplanationLoading(false));
    return () => ctrl.abort();
  }, [sessionId]);

  async function send(decision: "accept" | "reject") {
    setSubmitting(decision);
    setError(null);
    try {
      await submitDeployConfirmation(sessionId, { decision });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Submission failed.");
      setSubmitting(null);
    }
  }

  return (
    <section className="space-y-4">
      <header>
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          <Chevron className="mr-2" /> what-if
        </p>
        <h2 className="text-xl font-semibold mt-1">
          Review the deploy plan
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {plan.resources.length} resource{plan.resources.length === 1 ? "" : "s"}{" "}
          to {actionSummary(plan)}. Plan id: <span className="font-mono">{plan.deployment_id}</span>
        </p>
      </header>

      <aside className="rounded-md border border-primary/30 bg-primary/5 p-4">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
          <Chevron className="mr-2" /> what this deploy does
        </p>
        {explanationLoading ? (
          <p className="text-sm text-muted-foreground italic">Generating explanation…</p>
        ) : explanation ? (
          <p className="text-sm leading-relaxed">{explanation}</p>
        ) : (
          <p className="text-sm text-muted-foreground italic">(No explanation available.)</p>
        )}
      </aside>

      <table className="w-full rounded-lg border border-border/60 overflow-hidden text-sm">
        <thead className="bg-secondary/60 text-left text-muted-foreground">
          <tr>
            <th className="px-3 py-2 font-medium">Action</th>
            <th className="px-3 py-2 font-medium">Resource type</th>
            <th className="px-3 py-2 font-medium">Name</th>
          </tr>
        </thead>
        <tbody>
          {plan.resources.map((r, i) => (
            <tr key={`${r.name}-${i}`} className="border-t border-border/60">
              <td className="px-3 py-2">
                <ActionBadge action={r.action} />
              </td>
              <td className="px-3 py-2 font-mono text-xs">{r.resource_type}</td>
              <td className="px-3 py-2 font-mono text-xs">{r.name}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <details className="rounded-lg border border-border/60 bg-card">
        <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium">
          Raw what-if output
        </summary>
        <pre className="overflow-x-auto px-4 pb-4 text-xs font-mono leading-relaxed">
{plan.what_if_diff}
        </pre>
      </details>

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      <footer className="flex items-center justify-end gap-3 pt-2 border-t border-border/60">
        <button
          type="button"
          onClick={() => void send("reject")}
          disabled={submitting !== null}
          className="rounded-md border border-border bg-transparent px-4 py-2 text-sm font-medium hover:bg-secondary/60 disabled:opacity-50 transition-colors"
        >
          {submitting === "reject" ? "Cancelling…" : "Cancel"}
        </button>
        <button
          type="button"
          onClick={() => void send("accept")}
          disabled={submitting !== null}
          className="rounded-md bg-primary px-5 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {submitting === "accept" ? "Deploying…" : "Confirm and deploy"}
        </button>
      </footer>
    </section>
  );
}

function actionSummary(plan: DeploymentPlan): string {
  const counts: Record<string, number> = {};
  for (const r of plan.resources) {
    counts[r.action] = (counts[r.action] ?? 0) + 1;
  }
  return Object.entries(counts)
    .map(([action, n]) => `${n} ${action}`)
    .join(", ");
}

function ActionBadge({ action }: { action: string }) {
  const colour =
    action === "create"
      ? "bg-emerald-500/15 text-emerald-500"
      : action === "modify"
      ? "bg-amber-500/15 text-amber-500"
      : action === "delete"
      ? "bg-destructive/15 text-destructive"
      : "bg-muted text-muted-foreground";
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-mono uppercase tracking-wide ${colour}`}
    >
      {action}
    </span>
  );
}
